/* Templates: reusable report layouts. People's own saved templates (from
 * "Save as template" on a report) plus the platform's built-in starter
 * layouts. Rename / describe / categorise / delete; "Use template" starts a
 * new report from it. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, LayoutTemplate, MoreHorizontal, Pencil, Plus, Trash2, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { summarizeDefinition, templatesApi, type LayoutSummary, type ReportTemplateItem } from "../../api/v21";
import { useAuth } from "../../auth/AuthContext";
import { KindLegend, LayoutThumb, describeVisuals } from "../../components/LayoutThumb";
import { Badge, Button, Card, EmptyState, Field, Input, Menu, type MenuItem, Modal, PageHeader, SearchInput, Segmented, Skeleton, Textarea, formatRelative, useConfirm, useToast } from "../../ds";
import { modeFor } from "../../shell/role";
import { TEMPLATES, buildDefinition } from "./templates";

export function TemplatesPage() {
  const { user } = useAuth();
  const isAdmin = modeFor(user) === "admin";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [editing, setEditing] = useState<ReportTemplateItem | null>(null);
  const list = useQuery({ queryKey: ["templates"], queryFn: templatesApi.list });
  const categories = useMemo(() => [...new Set((list.data ?? []).map((t) => t.category).filter(Boolean) as string[])].sort(), [list.data]);
  const match = (t: { name: string; description: string | null; category: string | null }) =>
    (!q || `${t.name} ${t.description ?? ""} ${t.category ?? ""}`.toLowerCase().includes(q.toLowerCase())) && (category === "all" || t.category === category);
  const mine = (list.data ?? []).filter((t) => t.created_by === user?.id && match(t));
  const others = (list.data ?? []).filter((t) => t.created_by !== user?.id && match(t));
  const builtins = useMemo(() => TEMPLATES.map((t) => ({ t, summary: summarizeDefinition(buildDefinition(t, "preview", t.name)) })).filter(({ t }) => category === "all" && (!q || `${t.name} ${t.description}`.toLowerCase().includes(q.toLowerCase()))), [q, category]);

  const remove = useMutation({
    mutationFn: (t: ReportTemplateItem) => templatesApi.remove(t.id),
    onSuccess: (_d, t) => { qc.invalidateQueries({ queryKey: ["templates"] }); toast.success("Template deleted", t.name); },
    onError: (e) => toast.error("Couldn't delete the template", e instanceof Error ? e.message : undefined),
  });

  const use = (key: string) => navigate(`/admin/reports?new=1&template=${encodeURIComponent(key)}`);
  const menuFor = (t: ReportTemplateItem): MenuItem[] => [
    ...(isAdmin ? [{ label: "Use template", icon: Wand2, onSelect: () => use(`custom:${t.id}`) }] : []),
    ...(t.can_edit
      ? [
          { label: "Edit name & details", icon: Pencil, onSelect: () => setEditing(t) },
          {
            label: "Delete…",
            icon: Trash2,
            danger: true,
            onSelect: async () => {
              if (await confirm({ title: `Delete “${t.name}”?`, message: "Reports already made from it are not affected.", confirmLabel: "Delete template", tone: "danger" })) remove.mutate(t);
            },
          },
        ]
      : []),
  ];

  return (
    <div className="ds-page ds-page--wide" style={{ maxWidth: 1500 }}>
      <PageHeader
        eyebrow="Build faster"
        title="Templates"
        subtitle={isAdmin ? "Reusable report layouts. Save any report as a template from its page, then start new reports from it here or in New report." : "Reusable report layouts. Save any of your reports as a template from its page. New reports are created by an admin — they can start one from any template here."}
        actions={isAdmin ? <Button variant="primary" icon={Plus} onClick={() => navigate("/admin/reports?new=1")}>New report</Button> : null}
      />
      <div className="ds-toolbar">
        <SearchInput value={q} onChange={setQ} placeholder="Search templates" width={280} />
        {categories.length ? <Segmented<string> value={category} onChange={setCategory} options={[{ value: "all", label: "All" }, ...categories.map((c) => ({ value: c, label: c }))]} /> : null}
        <div className="ds-grow" />
        <KindLegend />
      </div>

      <Section title="Your templates" subtitle="Saved by you. Only you and admins can change them.">
        {list.isLoading ? <SkeletonGrid /> : mine.length === 0 ? (
          <Card>
            <EmptyState icon={LayoutTemplate} title={q || category !== "all" ? "Nothing matches" : "You haven't saved a template yet"} description={q || category !== "all" ? "Try another search." : "Open any report and choose “Save as template” — its layout (with or without its data connections) appears here, ready to reuse."} />
          </Card>
        ) : (
          <Grid>{mine.map((t) => <TemplateCard key={t.id} t={t} menu={menuFor(t)} onUse={isAdmin ? () => use(`custom:${t.id}`) : undefined} />)}</Grid>
        )}
      </Section>

      {others.length ? (
        <Section title={isAdmin ? "Saved by other people" : "Shared by other people"} subtitle={isAdmin ? "Admins can see and manage every template." : "Layout-only templates are shared with everyone who builds reports."}>
          <Grid>{others.map((t) => <TemplateCard key={t.id} t={t} menu={menuFor(t)} onUse={isAdmin ? () => use(`custom:${t.id}`) : undefined} />)}</Grid>
        </Section>
      ) : null}

      {builtins.length ? (
        <Section title="Built-in starter layouts" subtitle="Shipped with the platform: empty layouts with placeholder visuals that you connect to your own data. They can't be edited — create a report from one, adjust it, then save it as your own template.">
          <Grid>
            {builtins.map(({ t, summary }) => (
              <BuiltinCard key={t.id} name={t.name} description={t.description} summary={summary} onUse={isAdmin ? () => use(`builtin:${t.id}`) : undefined} />
            ))}
          </Grid>
        </Section>
      ) : null}

      <EditTemplateDialog t={editing} categories={categories} onClose={() => setEditing(null)} />
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16, margin: "0 0 2px" }}>{title}</h2>
      <p className="ds-small ds-muted" style={{ margin: "0 0 12px", maxWidth: 820 }}>{subtitle}</p>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="ds-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>{children}</div>;
}

function SkeletonGrid() {
  return <Grid>{[0, 1, 2].map((i) => <Card key={i}><Skeleton height={120} /><Skeleton width="60%" style={{ marginTop: 12 }} /></Card>)}</Grid>;
}

function TemplateCard({ t, menu, onUse }: { t: ReportTemplateItem; menu: MenuItem[]; onUse?: () => void }) {
  const [page, setPage] = useState(0);
  return (
    <Card>
      <LayoutThumb summary={t.summary} page={page} height={132} title={`${t.name} — ${t.summary.pages[page]?.name ?? "page"}`} />
      {t.summary.pageCount > 1 ? (
        <div className="ds-row" style={{ gap: 4, marginTop: 6 }}>
          <Button size="sm" variant="ghost" icon={ChevronLeft} disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page preview" />
          <span className="ds-small ds-muted ds-truncate ds-grow" style={{ textAlign: "center" }} title={t.summary.pages[page]?.name}>
            Page {page + 1} of {t.summary.pageCount} · {t.summary.pages[page]?.name}
          </span>
          <Button size="sm" variant="ghost" icon={ChevronRight} disabled={page >= t.summary.pages.length - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page preview" />
        </div>
      ) : null}
      <div className="ds-row" style={{ marginTop: 10, alignItems: "flex-start" }}>
        <div className="ds-grow" style={{ minWidth: 0 }}>
          <div className="ds-truncate" style={{ fontWeight: 650, fontSize: 15 }}>{t.name}</div>
          <div className="ds-row" style={{ gap: 6, marginTop: 4, flexWrap: "wrap" }}>
            {t.keeps_bindings ? <Badge tone="success" title="Visuals stay connected to the same datasets">Keeps data connections</Badge> : <Badge title="Visuals start empty — connect them to any data">Layout only</Badge>}
            {t.category ? <Badge tone="info">{t.category}</Badge> : null}
          </div>
        </div>
        {menu.length ? <Menu trigger={(p) => <Button {...p} size="sm" variant="ghost" icon={MoreHorizontal} aria-label="Template actions" />} items={menu} /> : null}
      </div>
      <div className="ds-small ds-muted" style={{ marginTop: 8, minHeight: 36 }}>{t.description || "No description."}</div>
      <div className="ds-small ds-faint" style={{ marginTop: 6 }}>{describeVisuals(t.summary.visualTypes)}{t.summary.pageCount > 1 ? ` · ${t.summary.pageCount} pages` : ""}</div>
      <div className="ds-row" style={{ marginTop: 10, justifyContent: "space-between" }}>
        <span className="ds-small ds-faint ds-truncate" style={{ minWidth: 0 }}>{t.created_by_name ? `${t.created_by_name} · ` : ""}{formatRelative(t.updated_at)}</span>
        {onUse ? <Button size="sm" icon={Wand2} onClick={onUse}>Use template</Button> : null}
      </div>
    </Card>
  );
}

function BuiltinCard({ name, description, summary, onUse }: { name: string; description: string; summary: LayoutSummary; onUse?: () => void }) {
  return (
    <Card>
      <LayoutThumb summary={summary} height={132} title={`${name} layout`} empty="Blank page" />
      <div className="ds-row" style={{ marginTop: 10, justifyContent: "space-between" }}>
        <span style={{ fontWeight: 650, fontSize: 15 }}>{name}</span>
        <Badge>Built-in</Badge>
      </div>
      <div className="ds-small ds-muted" style={{ marginTop: 6, minHeight: 36 }}>{description}</div>
      <div className="ds-row" style={{ marginTop: 10, justifyContent: "space-between" }}>
        <span className="ds-small ds-faint">{describeVisuals(summary.visualTypes)}</span>
        {onUse ? <Button size="sm" icon={Wand2} onClick={onUse}>Use template</Button> : null}
      </div>
    </Card>
  );
}

function EditTemplateDialog({ t, categories, onClose }: { t: ReportTemplateItem | null; categories: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [lastId, setLastId] = useState<string | null>(null);
  if (t && t.id !== lastId) {
    setLastId(t.id);
    setName(t.name);
    setDescription(t.description ?? "");
    setCategory(t.category ?? "");
  }
  const save = useMutation({
    mutationFn: () => templatesApi.update(t!.id, { name: name.trim(), description: description.trim() || null, category: category.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); toast.success("Template updated"); onClose(); setLastId(null); },
    onError: (e) => toast.error("Couldn't save", e instanceof Error ? e.message : undefined),
  });
  return (
    <Modal open={!!t} onClose={() => { onClose(); setLastId(null); }} title="Edit template" footer={<><Button onClick={() => { onClose(); setLastId(null); }}>Cancel</Button><Button variant="primary" loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>Save</Button></>}>
      <div className="ds-stack">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} /></Field>
        <Field label="Description" hint="Say what the layout is good for, e.g. “Monthly cost review with budget vs actual”."><Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={1024} /></Field>
        <Field label="Category" hint="Groups templates on this page, e.g. Finance, HR, Operations.">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} list="template-categories" maxLength={64} />
          <datalist id="template-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
        </Field>
      </div>
    </Modal>
  );
}
